import Tooltip from '@mui/material/Tooltip';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useEditor } from '../EditorContext';
import { getTimelineSegments } from '../editorUtils';
import { useAudioWaveform } from './useAudioWaveform';
import WaveformView from './WaveformView';

// Convert output (packed) time → original video time
function outToOriginal(outTime, displaySegs) {
    for (const seg of displaySegs) {
        if (outTime <= seg.outEnd) {
            return seg.startTime + Math.max(0, outTime - seg.outStart);
        }
    }
    return displaySegs.length ? displaySegs[displaySegs.length - 1].endTime : outTime;
}

// Convert original video time → output (packed) time; returns null if in a deleted range
function originalToOut(origTime, displaySegs) {
    for (const seg of displaySegs) {
        if (origTime >= seg.startTime && origTime <= seg.endTime) {
            return seg.outStart + (origTime - seg.startTime);
        }
    }
    return null;
}

/* ── Styles ─────────────────────────────────────────────────────── */

const TrackRow = styled.div`
    position: relative;
    height: 40px;
    background: #121218;
    border-bottom: 1px solid #2a2a3a;
    overflow: hidden;
`;

const SegmentBlock = styled.div`
    position: absolute;
    top: 4px;
    height: 32px;
    border-radius: 3px;
    box-sizing: border-box;
    cursor: ${({ tool, isDeleted }) => {
        if (isDeleted) return 'not-allowed';
        if (tool === 'razor') return 'crosshair';
        if (tool === 'select') return 'pointer';
        return 'grab';
    }};
    background: ${({ isDeleted, isSelected }) => {
        if (isDeleted) return 'rgba(183, 28, 28, 0.45)';
        if (isSelected) return 'linear-gradient(180deg, #3a6ea8 0%, #1e4976 100%)';
        return 'linear-gradient(180deg, #2979ff 0%, #1565c0 100%)';
    }};
    border: ${({ isDeleted, isSelected }) => {
        if (isDeleted) return '2px solid #b71c1c';
        if (isSelected) return '2px solid #e8a020';
        return '1px solid #0d47a1';
    }};
    opacity: ${({ isDeleted }) => (isDeleted ? 1 : 1)};
    overflow: hidden;
    white-space: nowrap;
    display: flex;
    align-items: center;
    padding: 0 6px;
`;

const SegmentLabel = styled.span`
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
    user-select: none;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const CutLine = styled.div`
    position: absolute;
    top: 0;
    width: 2px;
    height: 100%;
    background: #ff6f00;
    z-index: 5;
    cursor: col-resize;
    transform: translateX(-50%);

    &:hover {
        background: #ffd740;
        width: 3px;
    }
`;

const GhostLine = styled.div`
    position: absolute;
    top: 0;
    width: 2px;
    height: 100%;
    background: rgba(255, 215, 64, 0.6);
    border-left: 1px dashed #ffd740;
    z-index: 4;
    pointer-events: none;
    transform: translateX(-50%);
`;

const ContextMenu = styled.ul`
    position: fixed;
    z-index: 1000;
    background: #1e1e2e;
    border: 1px solid #3a3a5a;
    border-radius: 6px;
    padding: 4px 0;
    margin: 0;
    list-style: none;
    min-width: 190px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.5);
`;

const MenuItem = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    font-size: 12px;
    color: ${({ disabled }) => (disabled ? '#555' : '#ccc')};
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    white-space: nowrap;

    &:hover {
        background: ${({ disabled }) => (disabled ? 'transparent' : 'rgba(100,181,246,0.12)')};
        color: ${({ disabled }) => (disabled ? '#555' : '#90caf9')};
    }
`;

const MenuDivider = styled.li`
    height: 1px;
    background: #2a2a3a;
    margin: 3px 0;
`;

/* ── Component ───────────────────────────────────────────────────── */

/**
 * VideoTrack — renders the video segments as coloured blocks.
 * Deleted segments appear faded / hatched.
 *
 * Props:
 *   duration        — original video duration (seconds)
 *   cutMarkers      — [number] sorted cut points
 *   cuts            — [{ id, startTime, endTime }] ripple-deleted segments
 *   zoomLevel       — px/sec
 *   scrollLeft      — horizontal scroll offset
 *   activeTool      — 'select' | 'razor' | 'hand'
 *   selectedSegment — { startTime, endTime } | null
 *   playheadTime    — current playhead position on original timeline (seconds)
 *   videoTitle      — displayed as label
 *   onSegmentClick  — (seg) => void  — selects/deselects a segment
 *   onRazorClick    — (time) => void — adds a cut marker
 *   onRemoveMarker  — (time) => void — removes a cut marker
 */
export default function VideoTrack({
    duration,
    cutMarkers,
    cuts,
    zoomLevel,
    scrollLeft,
    activeTool,
    selectedSegment,
    playheadTime,
    videoTitle,
    onSegmentClick,
    onRazorClick,
    onRemoveMarker,
    onDeleteSegment,
    showDeleted = false,
    leftOffset = 0,
}) {
    const { state } = useEditor();
    const videoUrl = state.videoUrl || videoTitle || '';
    const waveformSamples = useAudioWaveform(state.videoUrl || null);
    const allSegs = getTimelineSegments(duration, cutMarkers, cuts);

    // Packed kept-only segments (used when showDeleted=false)
    let _runOut = 0;
    const packedSegs = allSegs
        .filter(seg => !seg.isDeleted)
        .map(seg => {
            const outStart = _runOut;
            const outEnd = outStart + (seg.endTime - seg.startTime);
            _runOut = outEnd;
            return { ...seg, outStart, outEnd };
        });

    // When showDeleted=true: all segs at original positions so deleted regions are visible
    const originalPosSeg = allSegs.map(seg => ({ ...seg, outStart: seg.startTime, outEnd: seg.endTime }));

    const displaySegs = showDeleted ? originalPosSeg : packedSegs;
    // For coord conversion we always use the packed segs (razor/playhead are in output time)
    const coordSegs = packedSegs;
    const [hoverX, setHoverX] = useState(null);
    const [ctxMenu, setCtxMenu] = useState(null); // { x, y, time, seg }
    const menuRef = useRef(null);

    // Close context menu on outside click or Escape
    useEffect(() => {
        if (!ctxMenu) return;
        const close = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setCtxMenu(null);
        };
        const onKey = (e) => { if (e.key === 'Escape') setCtxMenu(null); };
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('keydown', onKey);
        };
    }, [ctxMenu]);

    const handleTrackClick = (e) => {
        if (activeTool !== 'razor') return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left + scrollLeft - leftOffset;
        const outTime = x / zoomLevel;
        onRazorClick(parseFloat(outToOriginal(outTime, coordSegs).toFixed(3)));
    };

    const handleMouseMove = (e) => {
        if (activeTool !== 'razor') { setHoverX(null); return; }
        const rect = e.currentTarget.getBoundingClientRect();
        setHoverX(e.clientX - rect.left);
    };

    const handleMouseLeave = () => setHoverX(null);

    const handleTrackContextMenu = (e) => {
        e.preventDefault();
        // SegmentBlocks handle their own right-click; this fires for empty-space clicks
        setCtxMenu({ mouseX: e.clientX, mouseY: e.clientY, seg: null });
    };

    // Playhead (output time) is inside the right-clicked segment?
    const playheadInsideSeg = ctxMenu?.seg &&
        !ctxMenu.seg.isDeleted &&
        playheadTime > ctxMenu.seg.outStart &&
        playheadTime < ctxMenu.seg.outEnd;

    return (
        <TrackRow
            data-track-root
            onClick={handleTrackClick}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onContextMenu={handleTrackContextMenu}
        >
            {displaySegs.map((seg, idx) => {
                const left = seg.outStart * zoomLevel - scrollLeft + leftOffset;
                const width = (seg.outEnd - seg.outStart) * zoomLevel;
                if (left + width < 0 || left > 10000) return null;

                const isSelected =
                    !seg.isDeleted &&
                    selectedSegment &&
                    Math.abs(selectedSegment.startTime - seg.startTime) < 0.001 &&
                    Math.abs(selectedSegment.endTime - seg.endTime) < 0.001;

                const segNum = allSegs.indexOf(seg) + 1;
                const tooltipText = seg.isDeleted
                    ? `Deleted segment ${segNum}: ${formatDur(seg.endTime - seg.startTime)} · was ${formatDur(seg.startTime)}–${formatDur(seg.endTime)}`
                    : `Segment ${segNum}: ${formatDur(seg.endTime - seg.startTime)} · Right-click for options`;

                return (
                    <Tooltip key={idx} title={tooltipText} placement="top" arrow>
                        <SegmentBlock
                            tool={activeTool}
                            isDeleted={seg.isDeleted}
                            isSelected={isSelected}
                            style={{ left: `${left}px`, width: `${Math.max(2, width)}px`, position: 'absolute' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (activeTool === 'select' && !seg.isDeleted) {
                                    onSegmentClick(seg);
                                }
                            }}
                            onContextMenu={(e) => {
                                if (!seg.isDeleted) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setCtxMenu({ mouseX: e.clientX, mouseY: e.clientY, seg });
                                }
                            }}
                        >
                            {/* Waveform background */}
                            {!seg.isDeleted && width > 8 && (
                                <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
                                    <WaveformView
                                        seed={videoUrl}
                                        samples={waveformSamples}
                                        width={Math.max(4, width)}
                                        height={32}
                                        color={waveformSamples
                                            ? (isSelected ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.65)')
                                            : (isSelected ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.14)')}
                                    />
                                </div>
                            )}
                            {width > 40 && (
                                <SegmentLabel style={{ position: 'relative', zIndex: 1, color: seg.isDeleted ? 'rgba(255,100,100,0.8)' : undefined }}>
                                    {seg.isDeleted ? '🗑 deleted' : (videoTitle || 'Video')}
                                </SegmentLabel>
                            )}
                        </SegmentBlock>
                    </Tooltip>
                );
            })}

            {/* Razor hover ghost line */}
            {activeTool === 'razor' && hoverX !== null && (
                <GhostLine style={{ left: `${hoverX}px` }} />
            )}

            {/* Cut marker lines — show at packed positions when hidden, original when shown */}
            {cutMarkers.map((m) => {
                const outX = showDeleted ? m : originalToOut(m, coordSegs);
                if (outX === null) return null;
                const x = outX * zoomLevel - scrollLeft;
                if (x < -5 || x > 10000) return null;
                return (
                    <Tooltip key={m} title={`Cut at ${formatDur(m)} — double-click to remove`} placement="top">
                        <CutLine
                            style={{ left: `${x}px` }}
                            onDoubleClick={(e) => {
                                e.stopPropagation();
                                onRemoveMarker(m);
                            }}
                        />
                    </Tooltip>
                );
            })}

            {/* Right-click context menu */}
            {ctxMenu && (
                <ContextMenu
                    ref={menuRef}
                    style={{ top: ctxMenu.mouseY, left: ctxMenu.mouseX }}
                    onContextMenu={(e) => e.preventDefault()}
                >
                    {ctxMenu.seg ? (
                        <>
                            <MenuItem
                                disabled={!playheadInsideSeg}
                                onClick={() => {
                                    if (!playheadInsideSeg) return;
                                    onRazorClick(parseFloat(outToOriginal(playheadTime, coordSegs).toFixed(3)));
                                    setCtxMenu(null);
                                }}
                            >
                                ✂ Split at playhead &nbsp;
                                <span style={{ color: '#555', fontSize: 10 }}>
                                    {playheadInsideSeg ? `@ ${formatDur(playheadTime)}` : '(playhead not in segment)'}
                                </span>
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem
                                onClick={() => {
                                    onDeleteSegment(ctxMenu.seg);
                                    setCtxMenu(null);
                                }}
                            >
                                🗑 Delete segment
                            </MenuItem>
                        </>
                    ) : (
                        <MenuItem disabled>Right-click a segment for options</MenuItem>
                    )}
                </ContextMenu>
            )}
        </TrackRow>
    );
}

function formatDur(sec) {
    const m = Math.floor(sec / 60);
    const s = (sec % 60).toFixed(1);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
